import React, { useState, useMemo, useEffect } from "react"
import { StyleSheet } from "~/ui/primitives"
import { observer } from "mobx-react-lite"
import styled, { useTheme } from "~/ui/styled"
import QrcodeSvg from "~/ui/qrcode-svg"
import { useFormik } from "formik"
import * as Yup from "yup";
import { StartFlex, ColumnFlex, CenterFlex, BetweenFlex, IconFont, Text, Button } from "~components/El"
import { InlineInput } from "~components/InputField"
import { SecurityGoogleScreenProps } from "./types"
import { copyText, globalToast, globalSpinner }  from "~utils"
import { useStores } from "~models"
import useNavigation from "~hooks/useNavigation"


const Container = styled.View`
  flex: 1;
  padding: 10px 12px;
`

const SecretScrollWrap = styled.div`
  flex: 1;
  min-width: 0;
  margin-left: 10px;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  white-space: nowrap;
`
const SecurityGoogle: React.FC<SecurityGoogleScreenProps> = ({ route }) => {
    const theme = useTheme()
    const navigation = useNavigation()
    const { apiStore, walletStore } = useStores()
    const is_2fa_enabled = walletStore.is_2fa_enabled
    const [loading, setLoading] = useState(false)
    const [googleAuthInfo, setGoogleAuthInfo] = useState(null);

    const formik = useFormik({
        initialValues: {
            code: "",
        },
        validationSchema: () =>  Yup.object().shape({
            code: Yup.string().required("请输入验证码")
        }),
        onSubmit: (values) => {
            // console.log("提交成功", values);
            setLoading(true);
            if (googleAuthInfo?.is_bound === false) {
                apiStore.bindGoogleAuthCode({ code: values.code, secret: googleAuthInfo?.secret }).then((res) => {
                    walletStore.getWalletInfo();
                    apiStore.toggleGoogleAuth({ enabled: true, code: values.code }).then((res) => {
                        walletStore.getWalletInfo();
                        globalToast.show("谷歌验证已启用");
                        navigation.goBack();
                        setLoading(false);
                    }).catch((error) => {
                        setLoading(false);
                        globalToast.show(error?.message || "开启谷歌验证失败");
                    });
                }).catch((error) => {
                    setLoading(false);
                    globalToast.show(error?.message || "绑定谷歌验证码失败");
                })
            } else {
                apiStore.toggleGoogleAuth({ enabled: !is_2fa_enabled, code: values.code }).then((res) => {
                    walletStore.getWalletInfo();
                    globalToast.show(`谷歌验证已${is_2fa_enabled === true ? "禁用" : "启用"}`);
                    navigation.goBack();
                    setLoading(false);
                }).catch((error) => {
                    setLoading(false);
                    globalToast.show(error?.message || `${is_2fa_enabled === true ? "禁用" : "启用"}谷歌验证失败`);
                });
            }
            
        },
    });
    const { values, errors, touched, setFieldValue, handleSubmit } = formik;

    const isActive = useMemo(() => {
        return Boolean(values.code);
    }, [values.code]);
    const getGoogleAuthInfo = async () => {
        try {
            globalSpinner.show();
            const res = await apiStore.getGoogleAuthInfo();
            if (res?.data) {
                setGoogleAuthInfo(res.data);
            }
            globalSpinner.close();
        } catch (error) {
            globalSpinner.close();
            globalToast.show(error?.message || "获取谷歌验证码信息失败");
        }
    }

    useEffect(() => {
        getGoogleAuthInfo();
    }, []);

    return (
        <Container>
            <ColumnFlex
                height="auto"
                width="100%"
                padding="20px 12px 0 12px"
                borderRadius={theme.space.sm}
                bg={theme.colors.blockBackground}
            >
                {googleAuthInfo?.otpauth_uri && (
                    <CenterFlex flexDirection="column" width="100%">
                        <QrcodeSvg value={googleAuthInfo?.otpauth_uri || ""} size={120} ecl={"M"} />
                        <CenterFlex flexDirection="column" padding="10px 0">
                            <Text color={theme.colors.text} fontSize={theme.fontSizes.md} lineHeight="20px">
                                请使用谷歌验证其扫描二维码
                            </Text>
                            <Text color={theme.colors.text} fontSize={theme.fontSizes.md} lineHeight="20px">
                                或手动输入下方密钥
                            </Text>
                        </CenterFlex>
                    
                    </CenterFlex>
                )}
                {googleAuthInfo?.secret && (
                    <Button
                        flexDirection="row"
                        width="100%"
                        padding="20px 0"
                        justifyContent="space-between"
                        borderBottomWidth={StyleSheet.hairlineWidth}
                        borderBottomColor={theme.colors.border}
                        borderBottomStyle="solid"
                        onClick={() => copyText(googleAuthInfo?.secret || "")}
                    >
                        <StartFlex flex={1} minWidth={0} width="100%">
                            <IconFont name="ggmy" color={theme.colors.secondary} size={16} />
                            <SecretScrollWrap>
                                <Text color={theme.colors.text} fontSize={theme.fontSizes.md}>
                                    {googleAuthInfo?.secret || ""}
                                </Text>
                            </SecretScrollWrap>
                        </StartFlex>
                        <IconFont name="copy" color={theme.colors.primaryText} size={14} />
                    </Button>
                )}
                
                <StartFlex
                    flexDirection="row"
                    width="100%"
                >
                    <InlineInput
                        value={values.code}
                        placeholder="请输入6位数谷歌验证码"
                        keyboardType="number-pad"
                        maxLength={6}
                        onChangeText={(value) => setFieldValue("code", value)}
                        error={touched.code && errors.code}
                        prefix={<IconFont name="yzm" color={theme.colors.secondary} size={14} />}            
                    />
                </StartFlex>
            </ColumnFlex>
            <ColumnFlex
                height="auto"
                width="100%"
                padding="12px"
                mt="10px"
                borderRadius="8px"
                bg="#fff7ed"
            >
                <StartFlex height="auto" mb="4px">
                    <IconFont name="jinggao" color={theme.colors.error} size={14} />
                    <Text color="#9a3412" fontSize={theme.fontSizes.md} ml="8px">安全验证</Text>
                </StartFlex>
                <Text color="#9a3412" fontSize={theme.fontSizes.sm} lineHeight="18px" ml="22px">为了安全起见，您需填写谷歌验证码进行身份验证，禁用后不再使用谷歌验证器进行双重验证。</Text>
            </ColumnFlex>
            <BetweenFlex height="auto" width="100%" mt="10px">
                <Button
                    flex={1}
                    bg={theme.colors.primary}
                    borderRadius="10px"
                    height="40px"
                    loading={loading}
                    disabled={!isActive}
                    onClick={handleSubmit}
                >
                    <Text color={theme.colors.textLight} fontSize={theme.fontSizes.lg} fontWeight="600">
                        {is_2fa_enabled === null ? "完成设置" : is_2fa_enabled === true ? "确认禁用" : "确认启用"}
                    </Text>
                </Button>
            </BetweenFlex>
        </Container>
      )
}

export default observer(SecurityGoogle)
